import get from 'lodash/get'
import { sleep } from 'common/util'
import {
  Appliance,
  ApplianceConfiguration,
  ApplianceConnectionState,
  GeoApplianceResult,
  ListResult,
  Role,
  UpdateAppliancePayload,
  User,
} from 'common/api/v1/types'
import { WithRedirectBack } from './index'
import {
  AppliancesRequestParams,
  EnrichedAppliance,
  EnrichedApplianceWithOwner,
  PaginatedRequestParams,
} from '../../api/nm-types'
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { ThunkApi } from '../../store'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { getCurrentPage, getIsStreamManager, withDefaultPagination } from '../../utils'
import { getLocationParams } from '../../utils/params'

export const removeAppliance = createAsyncThunk<Appliance['id'], Appliance['id'], ThunkApi>(
  'appliance',
  async (id, { dispatch, extra: { api } }) => {
    try {
      await api.appliancesApi.removeAppliance(id)
      dispatch(getAppliances(withDefaultPagination(getLocationParams()) as Partial<AppliancesRequestParams>))
      return id
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'delete appliance' }))
      throw err
    }
  },
)

export const removeAppliances = createAsyncThunk<void, Appliance['id'][], ThunkApi>(
  'appliance/removeAppliances',
  async (ids, { dispatch, extra: { api } }) => {
    dispatch(enqueueSuccessSnackbar('Appliances are being deleted'))
    for (const id of ids) {
      try {
        await api.appliancesApi.removeAppliance(id)
      } catch (err) {
        dispatch(enqueueErrorSnackbar({ error: err, operation: 'delete appliance' }))
      }
    }
    dispatch(getAppliances(withDefaultPagination(getLocationParams()) as Partial<AppliancesRequestParams>))
  },
)

export const updateAppliance = createAsyncThunk<
  Appliance,
  { appliance: Appliance } & {
    values: UpdateAppliancePayload
  } & WithRedirectBack,
  ThunkApi
>(
  'appliance/updateAppliance',
  async ({ appliance, redirect, values }, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.appliancesApi.updateAppliance(appliance.id, values)
      redirect && navigate()(routes.appliances())
      dispatch(enqueueSuccessSnackbar(`Appliance ${appliance.name} updated`))
      dispatch(getAppliances(withDefaultPagination(getLocationParams()) as AppliancesRequestParams))
      return res
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'update appliance' }))
      throw err
    }
  },
)

export const getAppliances = createAsyncThunk<
  ListResult<EnrichedApplianceWithOwner>,
  Partial<AppliancesRequestParams>,
  ThunkApi
>('appliance/getAppliances', async (params, { dispatch, getState, extra: { api } }) => {
  try {
    return await api.appliancesApi.getAppliances(
      withDefaultPagination({
        ...params,
        isSystemProvider: get(getState(), 'value.userReducer.user.role') === Role.super,
      }),
    )
  } catch (err) {
    dispatch(enqueueErrorSnackbar({ error: err, operation: 'fetch appliances' }))
    throw err
  }
})

export const getGeoAppliances = createAsyncThunk<GeoApplianceResult, Partial<PaginatedRequestParams>, ThunkApi>(
  'appliance/getGeoAppliances',
  async (params, { dispatch, extra: { api } }) => {
    try {
      return await api.appliancesApi.getGeoAppliances(
        withDefaultPagination({
          ...params,
        }),
      )
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'fetch geo appliances' }))
      throw err
    }
  },
)

export const getAppliance = createAsyncThunk<EnrichedAppliance, Appliance['id'], ThunkApi>(
  'appliance/getAppliance',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.appliancesApi.getAppliance(id)
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'fetch appliance' }))
      throw 'Failed to fetch appliance'
    }
  },
)

export const getTotalRegions = createAsyncThunk<{ totalRegions: number }, undefined, ThunkApi>(
  'appliance/getTotalRegions',
  async (_, { extra: { api } }) => {
    const { total } = await api.regionApi.getRegions(withDefaultPagination({}))
    return { totalRegions: total }
  },
)

export const clearAppliance = createAction('appliances/clearAppliance')
export const clearAppliances = createAction('appliances/clearAppliances')

export const restartAppliance = createAsyncThunk<
  Appliance,
  { id: Appliance['id']; requestedVersion?: string; showSuccessSnackbar: boolean },
  ThunkApi
>('appliance/restartAppliance', async ({ id, requestedVersion, showSuccessSnackbar }, { dispatch, extra: { api } }) => {
  try {
    const res = await api.appliancesApi.restart(id, requestedVersion)
    if (showSuccessSnackbar) {
      dispatch(enqueueSuccessSnackbar('Appliance restarted'))
    }
    return res
  } catch (err) {
    dispatch(enqueueErrorSnackbar({ error: err, operation: 'restart appliance' }))
    throw err
  }
})

export const restartAppliances = createAsyncThunk<void, { ids: Appliance['id'][] }, ThunkApi>(
  'appliance/restartAppliances',
  async ({ ids }, { dispatch }) => {
    ids.forEach((id) => {
      dispatch(restartAppliance({ id, showSuccessSnackbar: false }))
    })
  },
)

export const migrateApplianceSerial = createAsyncThunk<void, { id: Appliance['id']; serial: string }, ThunkApi>(
  'appliance/migrateApplianceSerial',
  async ({ id, serial }, { dispatch, extra: { api } }) => {
    try {
      await api.appliancesApi.migrateSerial(id, serial)
      dispatch(enqueueSuccessSnackbar('Appliance serial migrated successfully'))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'migrate appliance serial' }))
      throw err
    }
  },
)

export const upgradeImageAppliance = createAsyncThunk<void, { id: Appliance['id']; imageVersion: string }, ThunkApi>(
  'appliance/upgradeImageAppliance',
  async ({ id, imageVersion }, { dispatch, extra: { api } }) => {
    try {
      await api.appliancesApi.upgradeImage(id, imageVersion)
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'upgrade appliance' }))
      throw err
    }
  },
)

export const upgradeImageAppliances = createAsyncThunk<
  void,
  { ids: Appliance['id'][]; imageVersion: string },
  ThunkApi
>('appliance/upgradeImageAppliances', async ({ ids, imageVersion }, { dispatch }) => {
  ids.forEach((id) => {
    dispatch(upgradeImageAppliance({ id, imageVersion }))
  })
  dispatch(enqueueSuccessSnackbar('Appliance upgrades started'))
})

export const migrateApplianceToEdgeOs = createAsyncThunk<void, { id: Appliance['id'] }, ThunkApi>(
  'appliance/migrateApplianceToEdgeOs',
  async ({ id }, { dispatch, extra: { api } }) => {
    try {
      await api.appliancesApi.migrateToEdgeOs(id)
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'migrate appliance to Edge OS' }))
      throw err
    }
  },
)

export const migrateAppliancesToEdgeOs = createAsyncThunk<void, { ids: Appliance['id'][] }, ThunkApi>(
  'appliance/migrateAppliancesToEdgeOs',
  async ({ ids }, { dispatch }) => {
    ids.forEach((id) => {
      dispatch(migrateApplianceToEdgeOs({ id }))
    })
    dispatch(enqueueSuccessSnackbar('Edge OS migrations started'))
  },
)

const UPGRADE_TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes, matching appliance-ui
const UPGRADE_GRACE_PERIOD_MS = 60 * 1000 // 1 minute grace period before considering "no command" as done

export interface UpgradeProgress {
  applianceId: Appliance['id']
  done: boolean
  failed?: boolean
}

export const pollUpgradeProgress = createAsyncThunk<UpgradeProgress[], void, ThunkApi>(
  'appliance/pollUpgradeProgress',
  async (_, { dispatch, getState, extra: { api } }) => {
    const upgradingAppliances = getState().appliancesReducer.appliances.filter((a) => a._upgrading)
    return Promise.all(
      upgradingAppliances.map(async (appliance): Promise<UpgradeProgress> => {
        const elapsed = Date.now() - (appliance._upgrading?.startedAt ?? Date.now())
        if (elapsed > UPGRADE_TIMEOUT_MS) {
          return { applianceId: appliance.id, done: true }
        }
        try {
          const commands = await api.appliancesApi.listApplianceCommands(appliance.id)
          const failedCommand = commands.find((c) => c.status.type === 'error')
          if (failedCommand) {
            const errorMessage = failedCommand.status.type === 'error' ? failedCommand.status.error : 'Unknown error'
            dispatch(
              enqueueErrorSnackbar({
                error: new Error(errorMessage),
                operation: `upgrade appliance ${appliance.name}`,
              }),
            )
            return { applianceId: appliance.id, done: true, failed: true }
          }
          const activeCommand = commands.find((c) => c.status.type === 'in-progress' || c.status.type === 'not-started')
          if (activeCommand) {
            return { applianceId: appliance.id, done: false }
          }
          if (elapsed < UPGRADE_GRACE_PERIOD_MS) {
            return { applianceId: appliance.id, done: false }
          }
          const isConnected = appliance.health?.state === ApplianceConnectionState.connected
          return { applianceId: appliance.id, done: isConnected }
        } catch {
          // Appliance likely offline during reboot — keep waiting
          return { applianceId: appliance.id, done: false }
        }
      }),
    )
  },
)

export const recreateTunnels = createAsyncThunk<void, { id: Appliance['id'] }, ThunkApi>(
  'appliance/recreateTunnels',
  async ({ id }, { dispatch, extra: { api } }) => {
    try {
      await api.appliancesApi.recreateTunnels(id)
      dispatch(enqueueSuccessSnackbar('Appliance tunnels recreated'))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'recreate appliance tunnels' }))
      throw err
    }
  },
)

export const getApplianceConfig = createAsyncThunk<ApplianceConfiguration, Appliance['id'], ThunkApi>(
  'appliance/getApplianceConfig',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.appliancesApi.getConfig(id)
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'fetch appliance config' }))
      throw err
    }
  },
)

export const clearCachedApplianceConfig = createAsyncThunk<void, Appliance['id'], ThunkApi>(
  'appliance/clearCachedApplianceConfig',
  async (id, { dispatch, extra: { api } }) => {
    try {
      await api.appliancesApi.clearCachedApplianceConfig(id)
      await dispatch(getApplianceConfig(id))
      dispatch(enqueueSuccessSnackbar('Appliance config regenerated'))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'regenerate appliance config' }))
      throw err
    }
  },
)

export const initAppliance = createAsyncThunk<void, void, ThunkApi>(
  'appliance/initAppliance',
  async (_, { getState, dispatch }) => {
    const user = getState().userReducer.user as User
    if ('id' in user && user.role !== Role.basic) {
      dispatch(getTotalRegions())
    }
  },
)

let hasStartedFetchingAppliances = false
export const registerApplianceObserver = createAsyncThunk<void, { applianceIds: string[] }, ThunkApi>(
  'appliance/registerApplianceObserver',
  async (_, { dispatch, getState, extra: { routes } }) => {
    if (!hasStartedFetchingAppliances) {
      hasStartedFetchingAppliances = true

      while (true) {
        // Await so it doesn't queue up requests if the backend is slow, now it will sleep 10s after each fetch
        await sleep(10 * 1000)
        if (
          !getIsStreamManager(getCurrentPage(), routes) &&
          !!(getState().userReducer.user as User).id &&
          !getState().userReducer.changingUser
        ) {
          await dispatch(getBareAppliances())
          if (getState().appliancesReducer.appliances.some((a) => a._upgrading)) {
            await dispatch(pollUpgradeProgress())
          }
        }
      }
    }
  },
)

export const unregisterApplianceObserver = createAction<{ applianceIds: string[] }>(
  'appliance/unregisterApplianceObserver',
)

export const getBareAppliances = createAsyncThunk<ListResult<Appliance>, void, ThunkApi>(
  'appliances/getBareAppliances',
  async (_, { getState, extra: { api } }) => {
    const applianceIds = Object.keys(getState().appliancesReducer.appliancesToObserve)
    if (applianceIds.length > 0) {
      return await api.appliancesApi.listAppliances({ filter: { ids: applianceIds } })
    } else {
      return { total: 0, items: [] }
    }
  },
)
